import {api, setAdminApiTarget} from './api.js';
import {apiUrl} from './config.js';

export type AdminEnvironment = 'production' | 'test';
type TestLease = {environment: 'test'; apiOrigin: string; token: string; expiresAt: string};

const STORAGE_KEY = 'tax-assistant.admin.environment';
let activeEnvironment: AdminEnvironment = 'production';

function selectedEnvironment(): AdminEnvironment {
  return sessionStorage.getItem(STORAGE_KEY) === 'test' ? 'test' : 'production';
}

async function activateTest(): Promise<void> {
  // This call is same-origin and carries the existing production admin session.
  // The returned lease is intentionally held only in memory, never in storage.
  setAdminApiTarget(null);
  const lease = await api.post<TestLease>(apiUrl('/api/admin/environment-bridge/test/token'));
  if (lease.environment !== 'test' || !lease.apiOrigin || !lease.token) throw new Error('测试环境管理授权未成功签发');
  setAdminApiTarget({origin: lease.apiOrigin, token: lease.token});
}

export async function restoreAdminEnvironment(): Promise<AdminEnvironment> {
  const desired = selectedEnvironment();
  if (desired === 'test') {
    try {
      await activateTest();
      activeEnvironment = 'test';
      return activeEnvironment;
    } catch {
      // Fail back to production rather than showing a test page with stale or
      // unauthenticated data. The operator can try the switch again manually.
      sessionStorage.removeItem(STORAGE_KEY);
      setAdminApiTarget(null);
    }
  }
  activeEnvironment = 'production';
  return activeEnvironment;
}

export function currentAdminEnvironment(): AdminEnvironment {
  return activeEnvironment;
}

export async function switchAdminEnvironment(environment: AdminEnvironment): Promise<void> {
  if (environment === 'test') await activateTest();
  else setAdminApiTarget(null);
  activeEnvironment = environment;
  sessionStorage.setItem(STORAGE_KEY, environment);
}

/**
 * 在测试环境管理接口上执行一次请求，然后回到当前环境。
 * 正式环境保存模板后，用它把同一份配置同步到测试站。
 * @param work 要执行的请求
 */
export async function runAgainstTestAdmin<T>(work: () => Promise<T>): Promise<T> {
  if (activeEnvironment === 'test') return work();
  await activateTest();
  try {
    return await work();
  } finally {
    setAdminApiTarget(null);
  }
}
